/**
 * Inline SVG icon set — 24×24, 1.5px stroke, round caps. No emoji anywhere in
 * the app surface, and no icon font: the set is small and the stroke colour has
 * to follow the zone scale at runtime.
 */
import React from 'react';
import { c } from './tokens';

const S = {
    fill: 'none',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
};

type Draw = (stroke: string) => React.ReactNode;

export const ICONS: Record<string, Draw> = {
    /* 8 directions */
    cognition: (k) => (
        <g {...S} stroke={k}>
            <circle cx="10.5" cy="13" r="6.5" />
            <circle cx="19" cy="6" r="2.2" />
        </g>
    ),
    affect: (k) => (
        <path
            {...S}
            stroke={k}
            d="M3 14.5 C5.5 14.5 6 8.5 9 8.5 C12 8.5 12.5 15.5 15.5 15.5 C18.2 15.5 18.8 11 21 10"
        />
    ),
    attention: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <circle cx="12" cy="12" r="3.6" />
            <circle cx="12" cy="12" r="1" fill={k} stroke="none" />
        </g>
    ),
    self: (k) => (
        <g {...S} stroke={k}>
            <path d="M6.5 13.5 A5.5 5.5 0 0 1 17.5 13.5" />
            <path d="M12 4.5 V13.5" />
            <path d="M5 18.5 H19" />
        </g>
    ),
    motivation: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <path d="M15.6 8.4 L10.6 10.6 L8.4 15.6 L13.4 13.4 Z" />
        </g>
    ),
    behavior: (k) => (
        <g {...S} stroke={k}>
            <path d="M4 19 H9 V14 H14 V9 H19" />
            <path d="M15.6 5.4 L19.4 9.2 L15.6 13" />
        </g>
    ),
    biophysiology: (k) => (
        <g {...S} stroke={k}>
            <path d="M3 12.5 H7 L9 8 L12 17 L14.5 12.5 H21" />
        </g>
    ),
    social: (k) => (
        <g {...S} stroke={k}>
            <circle cx="9" cy="12" r="5.3" />
            <circle cx="15" cy="12" r="5.3" />
        </g>
    ),

    /* 5 tabs */
    tState: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <circle cx="12" cy="12" r="2.7" fill={k} stroke="none" />
        </g>
    ),
    tDirs: (k) => (
        <g {...S} stroke={k}>
            <path d="M12 3.5 V20.5 M3.5 12 H20.5 M6.2 6.2 L17.8 17.8 M17.8 6.2 L6.2 17.8" />
        </g>
    ),
    tPats: (k) => (
        <g {...S} stroke={k}>
            <path d="M3 16.5 C6 16.5 6 7.5 9 7.5 C12 7.5 12 16.5 15 16.5 C17.7 16.5 18.6 12.6 19.5 9.5" />
        </g>
    ),
    tIns: (k) => (
        <g {...S} stroke={k}>
            <path d="M12 3.2 L14 9.6 L20.5 11.6 L14 13.6 L12 20.4 L10 13.6 L3.5 11.6 L10 9.6 Z" />
        </g>
    ),
    tTasks: (k) => (
        <g {...S} stroke={k}>
            <path d="M6 4.5 H14 L18 8.5 V19.5 H6 Z" />
            <path d="M13.8 4.6 V8.4 H17.8" />
            <path d="M9 13.2 L11 15.2 L15 10.9" />
        </g>
    ),

    /* utility */
    chev: (k) => <path {...S} stroke={k} d="M9 5 L16 12 L9 19" />,
    back: (k) => <path {...S} stroke={k} d="M15 5 L8 12 L15 19" />,
    down: (k) => <path {...S} stroke={k} d="M5 9 L12 16 L19 9" />,
    close: (k) => <path {...S} stroke={k} d="M6 6 L18 18 M18 6 L6 18" />,
    check: (k) => <path {...S} stroke={k} strokeWidth="2" d="M5 12.5 L9.5 17 L19 7" />,
    thumbUp: (k) => (
        <g {...S} stroke={k}>
            <rect x="2.6" y="10.4" width="4.6" height="10.4" rx="1.3" />
            <path d="M7.2 10.9 L11.1 2.6 A2.5 2.5 0 0 1 13.7 5.1 V9.2 H18.4 A2 2 0 0 1 20.4 11.5 L19.2 18.7 A2.2 2.2 0 0 1 17 20.8 H7.2 Z" />
        </g>
    ),
    /* the same hand, mirrored about the middle — arc sweeps flip with it */
    thumbDown: (k) => (
        <g {...S} stroke={k}>
            <rect x="2.6" y="3.2" width="4.6" height="10.4" rx="1.3" />
            <path d="M7.2 13.1 L11.1 21.4 A2.5 2.5 0 0 0 13.7 18.9 V14.8 H18.4 A2 2 0 0 0 20.4 12.5 L19.2 5.3 A2.2 2.2 0 0 0 17 3.2 H7.2 Z" />
        </g>
    ),
    plus: (k) => <path {...S} stroke={k} d="M12 5 V19 M5 12 H19" />,
    flame: (k) => (
        <path
            {...S}
            stroke={k}
            d="M12 3.5 C12 8 7.5 9.5 7.5 14 A4.5 4.5 0 0 0 16.5 14 C16.5 11.5 14.8 10.3 14.2 8.2 C13.4 9.6 12 9.4 12 3.5 Z"
        />
    ),
    info: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <path d="M12 10.6 V16.4" />
            <circle cx="12" cy="7.9" r=".9" fill={k} stroke="none" />
        </g>
    ),
    arrow: (k) => (
        <g {...S} stroke={k}>
            <path d="M4 12 H19 M13.5 6.5 L19 12 L13.5 17.5" />
        </g>
    ),
    report: (k) => (
        <g {...S} stroke={k}>
            <path d="M6 4.5 H14 L18 8.5 V19.5 H6 Z" />
            <path d="M13.8 4.6 V8.4 H17.8" />
            <path d="M9 13.2 L11 15.2 L15 10.9" />
        </g>
    ),
    clock: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <path d="M12 7.4 V12 L15.4 14" />
        </g>
    ),
    lock: (k) => (
        <g {...S} stroke={k}>
            <rect x="6" y="10.5" width="12" height="8.5" rx="2" />
            <path d="M8.5 10.5 V7.5 A3.5 3.5 0 0 1 15.5 7.5 V10.5" />
        </g>
    ),
    help: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="12" r="8.4" />
            <path d="M9.8 9.4 A2.3 2.3 0 1 1 12.3 12.2 V13.6" />
            <circle cx="12.3" cy="16.4" r=".85" fill={k} stroke="none" />
        </g>
    ),
    book: (k) => (
        <g {...S} stroke={k}>
            <path d="M9.5 3.5 V9 L5.6 17.2 A2 2 0 0 0 7.4 20.2 H16.6 A2 2 0 0 0 18.4 17.2 L14.5 9 V3.5 Z" />
            <path d="M8.5 3.5 H15.5 M7.4 13.6 H16.6" />
        </g>
    ),
    user: (k) => (
        <g {...S} stroke={k}>
            <circle cx="12" cy="8.6" r="3.8" />
            <path d="M5 20 C5 16.2 8.1 14.4 12 14.4 C15.9 14.4 19 16.2 19 20" />
        </g>
    ),

    /* paywall funnel */
    up: (k) => <path {...S} stroke={k} d="M12 19 V5 M6.5 10.5 L12 5 L17.5 10.5" />,
    /** Leaving our surface — used next to the billing-portal line. */
    out: (k) => (
        <g {...S} stroke={k}>
            <path d="M13.5 5 H19 V10.5 M19 5 L11.5 12.5" />
            <path d="M17 14.5 V18 A1.5 1.5 0 0 1 15.5 19.5 H6 A1.5 1.5 0 0 1 4.5 18 V8.5 A1.5 1.5 0 0 1 6 7 H9.5" />
        </g>
    ),
    card: (k) => (
        <g {...S} stroke={k}>
            <rect x="3" y="6" width="18" height="12" rx="2.2" />
            <path d="M3 10.2 H21" />
        </g>
    ),
    star: (k) => <path d="M12 3.2 L14.7 9.1 L21 9.9 L16.4 14.2 L17.6 20.5 L12 17.4 L6.4 20.5 L7.6 14.2 L3 9.9 L9.3 9.1 Z" fill={k} />,
};

export interface IconProps {
    k: string;
    size?: number;
    color?: string;
    style?: React.CSSProperties;
}

export const Icon: React.FC<IconProps> = ({ k, size = 20, color = c.textPri, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style} aria-hidden focusable="false">
        {ICONS[k] ? ICONS[k](color) : null}
    </svg>
);
